import React, { useState } from "react";

const load = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

interface Factor {
  label: string;
  points: number;
}

export default function DependencyPricing({ user }: { user?: any }) {
  const [base, setBase] = useState<number>(() => {
    try {
      return parseFloat(localStorage.getItem("dependency_base_rate") || "120");
    } catch {
      return 120;
    }
  });
  const residents = load("residents_records");
  const medsAll = load("meds_register");
  const marAll = load("mar_entries");
  const woundsAll = load("rmacs_wound_vault");
  const wellbeingAll = load("wellbeing_records");
  const mcaAll = load("mca_hub_records");
  const eolAll = load("eol_records");
  const [message, setMessage] = useState("");

  const saveBase = (v: number) => {
    setBase(v);
    localStorage.setItem("dependency_base_rate", String(v));
  };

  const factorsFor = (r: any): Factor[] => {
    const f: Factor[] = [];
    const meds = medsAll.filter((m: any) => m.residentName === r.name);
    if (meds.length >= 13) f.push({ label: `High medication count (${meds.length})`, points: 30 });
    else if (meds.length >= 9) f.push({ label: `Medication count (${meds.length})`, points: 20 });
    else if (meds.length >= 5) f.push({ label: `Medication count (${meds.length})`, points: 10 });
    if (meds.some((m: any) => m.isCD)) f.push({ label: "Controlled drugs", points: 10 });
    if (meds.some((m: any) => m.isCovert)) f.push({ label: "Covert medication (MCA)", points: 15 });
    const mar = marAll.filter((m: any) => m.residentName === r.name && m.status !== "Not required");
    if (mar.length) {
      const pct = Math.round((mar.filter((m: any) => m.status === "Given").length / mar.length) * 100);
      if (pct < 90) f.push({ label: `MAR compliance ${pct}% (extra monitoring)`, points: 10 });
    }
    const wounds = woundsAll.filter((w: any) => w.resident_name === r.name);
    if (wounds.length) {
      f.push({ label: `Wound chart (${wounds.length})`, points: 20 });
      if (wounds.some((w: any) => String(w.ulcer_stage || "").includes("Category 3") || String(w.ulcer_stage || "").includes("Category 4"))) f.push({ label: "Category 3/4 ulcer", points: 10 });
      const wl = Math.max(0, ...wounds.map((w: any) => parseFloat(w.waterlow_score) || 0));
      if (wl >= 20) f.push({ label: `Waterlow ${wl} (very high risk)`, points: 15 });
    }
    const wb = wellbeingAll.filter((w: any) => w.residentName === r.name).sort((a: any, b: any) => a.month.localeCompare(b.month));
    const lastWb = wb[wb.length - 1];
    if (lastWb) {
      if ((parseFloat(lastWb.pain) || 0) >= 2) f.push({ label: "Significant pain", points: 10 });
      if ((parseFloat(lastWb.mood) || 3) <= 2 || (parseFloat(lastWb.engagement) || 3) <= 2) f.push({ label: "Low mood / engagement", points: 10 });
    }
    if (mcaAll.some((m: any) => m.residentName === r.name)) f.push({ label: "MCA / DoLS involvement", points: 10 });
    if (eolAll.some((e: any) => e.residentName === r.name)) f.push({ label: "End-of-life care", points: 15 });
    return f;
  };

  const bandFor = (score: number) =>
    score >= 80 ? { name: "Very High", uplift: 45 } :
    score >= 50 ? { name: "High", uplift: 30 } :
    score >= 20 ? { name: "Medium", uplift: 15 } :
    { name: "Low", uplift: 0 };

  const rows = residents.map((r: any) => {
    const f = factorsFor(r);
    const score = f.reduce((s, x) => s + x.points, 0);
    const band = bandFor(score);
    return { r, f, score, band, daily: base + band.uplift, monthly: Math.round((base + band.uplift) * 30) };
  }).sort((a: any, b: any) => b.score - a.score);

  const saveRecommendation = (row: any) => {
    const next = residents.map((r: any) => (r.id === row.r.id ? { ...r, dependencyScore: row.score, dependencyBand: row.band.name, dependencyRate: row.daily } : r));
    localStorage.setItem("residents_records", JSON.stringify(next));
    setMessage(`Recommendation saved for ${row.r.name}: ${row.band.name} band, £${row.daily}/day.`);
  };

  const applyFee = (row: any) => {
    if (!window.confirm(`Set ${row.r.name}'s total monthly fee to £${row.monthly}? This feeds the invoice.`)) return;
    const next = residents.map((r: any) => (r.id === row.r.id ? { ...r, totalFee: String(row.monthly), dependencyScore: row.score, dependencyBand: row.band.name, dependencyRate: row.daily } : r));
    localStorage.setItem("residents_records", JSON.stringify(next));
    setMessage(`Monthly fee for ${row.r.name} set to £${row.monthly}. Regenerate invoices to apply.`);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">eMAR-to-Invoice Dependency Pricing</h2>
        <p className="text-sm text-slate-600">Transparent, evidenced pricing: each resident's clinical load becomes a dependency score, band and suggested fee that flows into invoicing.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="mb-1 block text-xs font-medium text-slate-600">Base daily rate (£) - band uplifts added on top</label>
        <input type="number" value={base} onChange={e => saveBase(parseFloat(e.target.value) || 0)} className="w-40 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        <div className="mt-2 text-xs text-slate-500">Bands: Low +£0 | Medium +£15 | High +£30 | Very High +£45 per day.</div>
      </div>

      <div className="space-y-3">
        {rows.map(row => (
          <div key={row.r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-slate-900">{row.r.name} <span className="text-xs text-slate-500">Room {row.r.room || "-"}</span></div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${row.band.name === "Very High" ? "bg-red-100 text-red-700" : row.band.name === "High" ? "bg-amber-100 text-amber-700" : row.band.name === "Medium" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{row.band.name} | score {row.score}</span>
                <span className="text-sm font-semibold text-slate-900">£{row.daily}/day | £{row.monthly}/month</span>
              </div>
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-slate-600">Show pricing factors</summary>
              <div className="mt-1 space-y-1">
                {row.f.length ? row.f.map(f => (
                  <div key={f.label} className="flex items-center justify-between text-xs text-slate-700"><span>{f.label}</span><span className="font-semibold">+{f.points}</span></div>
                )) : <div className="text-xs text-slate-500">No dependency factors - base rate applies.</div>}
              </div>
            </details>
            <div className="mt-2 flex gap-2">
              <button onClick={() => saveRecommendation(row)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Save recommendation</button>
              <button onClick={() => applyFee(row)} className="rounded-lg bg-green-700 px-3 py-1 text-sm text-white">Apply monthly fee to invoice</button>
            </div>
          </div>
        ))}
        {rows.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No residents yet.</div> : null}
      </div>
    </div>
  );
}